/** Cookie-changing auth requests must finish in order, including cancelled OTPs.
 * The browser applies Set-Cookie before JavaScript can reject stale credentials.
 */
let localQueue: Promise<unknown> = Promise.resolve();
const API_URL = import.meta.env.VITE_API_URL || '';

export function withAuthCookieLock<T>(operation: () => Promise<T>): Promise<T> {
  const run = async () => {
    if (typeof navigator === 'undefined' || !navigator.locks?.request) return operation();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15_000);
    try {
      return await navigator.locks.request(`geniac-refresh:${API_URL}`, {signal: controller.signal}, () => {
        // This timeout bounds acquisition only. The operation owns its deadline.
        clearTimeout(timer);
        return operation();
      });
    } finally {
      clearTimeout(timer);
    }
  };
  const result = localQueue.then(run, run);
  localQueue = result.catch(() => undefined);
  return result;
}

/** Bound both fetch and response-body consumption on browsers without timeout(). */
export async function authRequest<T>(
  path: string,
  options: RequestInit,
  consume: (response: Response) => Promise<T>,
  timeoutMs = 15_000,
): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new DOMException(
    'The sign-in service took too long. Please try again.', 'TimeoutError',
  )), timeoutMs);
  try {
    const response = await fetch(`${API_URL}${path}`, {...options, signal: controller.signal});
    return await consume(response);
  } finally {
    clearTimeout(timer);
  }
}
